import cx from 'classnames';
import React from 'react';

import { proseClasses, textClasses } from '../../colors';
import { pick } from '../../helpers/utils/object';
import { ALIGNMENTS, FONTS, SIZES, TextElement } from '../../types';
import { ColorType } from '../../types';
import { BackgroundProps } from './Background';

export const ALIGN_OPTIONS = pick(ALIGNMENTS, 'left', 'center', 'right', 'auto');
export type AlignType = keyof typeof ALIGN_OPTIONS;

export const SIZE_OPTIONS = pick(SIZES, 'sm', 'md', 'lg', 'xl', 'xxl');
export type SizeType = keyof typeof SIZE_OPTIONS;

export const FONT_OPTIONS = pick(FONTS, 'heading', 'sans', 'mono');
export type FontType = keyof typeof FONT_OPTIONS;

export type TextProps = {
  children: React.ReactElement | React.ReactNode;
  as?: TextElement;
  align?: AlignType;
  size?: SizeType;
  color?: ColorType;
  background?: ColorType;
  className?: string;
  font?: FontType;
} & Partial<BackgroundProps>;

const alignClasses: Record<AlignType, string> = {
  auto: '',
  left: 'text-left',
  center: 'text-center mx-auto',
  right: 'text-right ml-auto',
};

const sizeClasses: Record<SizeType, string> = {
  sm: 'prose-sm md:prose-md',
  md: 'prose-md md:prose-lg',
  lg: 'prose-lg md:prose-xl',
  xl: 'prose-xl md:prose-2xl',
  xxl: 'prose-xl md:prose-3xl xl:prose-p:text-3xl text-3xl',
};

const fontClasses: Record<FontType, string> = {
  sans: 'font-sans',
  mono: 'font-mono',
  heading: 'font-heading',
};

export const Text = ({
  children,
  as = 'p',
  background,
  color,
  align = 'left',
  size = 'lg',
  className,
  font = 'sans',
}: TextProps) => {
  const Element = as;

  return (
    <div className={className}>
      <Element
        className={cx(
          'break-words prose prose-a:hover:underline font-regular leading-relaxed prose-strong:font-medium prose-a:text-current',
          alignClasses[align],
          sizeClasses[size],
          fontClasses[font],
          color
            ? textClasses[color]
            : background
            ? proseClasses[background]
            : 'text-current',
          {
            ['prose-ul:text-left prose-ul:inline-block [&>*>ul>li>ul]:block [&>ul>li>ul]:block [&>ul>li>ul>li>ul]:block']:
              align === 'center',
            ['prose-ol:text-left prose-ol:inline-block [&>*>ol>li>ol]:block [&>ol>li>ol]:block [&>ol>li>ol>li>ol]:block']:
              align === 'center',
          },
        )}
      >
        {children}
      </Element>
    </div>
  );
};

export const TextMemo = React.memo(Text);
